import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Icon,
  Button,
  Card,
  CardHeader,
  CardBody,
  Input,
  Badge,
  showToast,
  Select,
} from '@repo/ui';
import { PageLayout } from '@xtnd/web/shared/layout';
import {
  useBankTransactionsReplicationMetadata,
  useUpdateBankTransactionsReplicationMetadata,
  useSyncBankTransactionFromClio,
  usePreviewMatterBankTransactions,
  useSyncMatterBankTransactions,
  usePreviewBankAccountTransactions,
  useSyncBankAccountTransactions,
} from '../queries';
import {
  dataReplicationApi,
  type BankTransactionSearchType,
  type BankTransactionSearchResult,
} from '../api';
import { BankTransactionPreviewTable } from '../components/BankTransactionPreviewTable';
import { canResetReplication } from '../utils/accessControl';
import { pickReplicationTimestamp } from '../utils/replication';
import { useSearchMatters, useSearchBankAccounts } from '@xtnd/web/shared/queries/useClioSearch';
import type { MatterSearchResult, BankAccountSearchResult, MatterSearchType, BankAccountSearchType } from '@xtnd/web/shared/api/clioSearchApi';

type SyncMode = 'individual' | 'matter' | 'bankAccount';

type PreviewData = {
  totalNewRecords: number;
  totalExistingRecords: number;
  newTransactions: any[];
  existingTransactions: any[];
  lastFiveLocal: any[];
} | null;

export default function ClioBankTransactionsReplicationPageEnhanced() {
  const navigate = useNavigate();

  // Sync mode state
  const [syncMode, setSyncMode] = useState<SyncMode>('individual');

  // Individual transaction state
  const [searchValue, setSearchValue] = useState('');
  const [searchType, setSearchType] = useState<BankTransactionSearchType>('bankTransactionId');
  const [isSearching, setIsSearching] = useState(false);
  const [dynamoDBResult, setDynamoDBResult] = useState<BankTransactionSearchResult | null>(null);
  const [clioResult, setClioResult] = useState<BankTransactionSearchResult | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Matter sync state
  const [matterSearchType, setMatterSearchType] = useState<MatterSearchType>('displayNumber');
  const [matterSearchValue, setMatterSearchValue] = useState('');
  const [matterSearchResults, setMatterSearchResults] = useState<MatterSearchResult[]>([]);
  const [selectedMatter, setSelectedMatter] = useState<MatterSearchResult | null>(null);
  const [matterDateFrom, setMatterDateFrom] = useState('');
  const [matterDateTo, setMatterDateTo] = useState('');

  // Bank account sync state
  const [bankAccountSearchType, setBankAccountSearchType] = useState<BankAccountSearchType>('name');
  const [bankAccountSearchValue, setBankAccountSearchValue] = useState('');
  const [bankAccountSearchResults, setBankAccountSearchResults] = useState<BankAccountSearchResult[]>([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccountSearchResult | null>(null);
  const [accountDateFrom, setAccountDateFrom] = useState('');
  const [accountDateTo, setAccountDateTo] = useState('');
  const [accountMatterId, setAccountMatterId] = useState('');

  // Preview state
  const [previewData, setPreviewData] = useState<PreviewData>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Queries and mutations
  const { data: metadataResponse, isLoading: isLoadingMetadata, refetch } = useBankTransactionsReplicationMetadata();
  const updateMetadataMutation = useUpdateBankTransactionsReplicationMetadata();
  const syncBankTransactionMutation = useSyncBankTransactionFromClio();
  
  const searchMattersMutation = useSearchMatters();
  const searchBankAccountsMutation = useSearchBankAccounts();
  
  const previewMatterMutation = usePreviewMatterBankTransactions();
  const syncMatterMutation = useSyncMatterBankTransactions();
  
  const previewBankAccountMutation = usePreviewBankAccountTransactions();
  const syncBankAccountMutation = useSyncBankAccountTransactions();

  const metadata = metadataResponse?.metadata;
  const bankTransactionsCount = metadataResponse?.bankTransactionsCount ?? 0;
  const canReset = canResetReplication();

  // Format date helper
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  // Handle reset metadata
  const handleResetMetadata = async () => {
    try {
      await updateMetadataMutation.mutateAsync(null);
    } catch (error) {
      // Error handled in mutation hook
    }
  };

  // ==========================================
  // INDIVIDUAL TRANSACTION HANDLERS
  // ==========================================

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      showToast.error('Please enter a search value');
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    setDynamoDBResult(null);
    setClioResult(null);

    try {
      const dbResult = await dataReplicationApi.searchBankTransactionInDynamoDB(searchValue.trim(), searchType);
      setDynamoDBResult(dbResult);

      if (!dbResult.found) {
        const clResult = await dataReplicationApi.searchBankTransactionInClio(searchValue.trim(), searchType);
        setClioResult(clResult);
      }
    } catch (error: any) {
      showToast.error(`Search failed: ${error.message}`);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSyncBankTransaction = async (transactionId: string) => {
    try {
      await syncBankTransactionMutation.mutateAsync(transactionId);
      await handleSearch();
    } catch (error) {
      // Error handled in mutation hook
    }
  };

  // ==========================================
  // MATTER SEARCH HANDLERS
  // ==========================================

  const handleSearchMatters = async () => {
    if (!matterSearchValue.trim()) {
      showToast.error('Please enter a search value');
      return;
    }

    try {
      const result = await searchMattersMutation.mutateAsync({
        searchType: matterSearchType,
        searchValue: matterSearchValue.trim(),
        limit: 20,
      });

      setMatterSearchResults(result.matters);
      
      if (result.matters.length === 0) {
        showToast.info('No matters found matching your search');
      } else {
        showToast.success(`Found ${result.matters.length} matter(s)`);
      }
    } catch (error) {
      showToast.error('Failed to search matters');
    }
  };

  const handleSelectMatter = (matter: MatterSearchResult) => {
    setSelectedMatter(matter);
    setMatterSearchResults([]);
    setMatterSearchValue('');
    showToast.success(`Selected: ${matter.displayNumber} - ${matter.description}`);
  };

  const handleClearMatterSelection = () => {
    setSelectedMatter(null);
    setPreviewData(null);
    setShowPreview(false);
  };

  // ==========================================
  // MATTER SYNC HANDLERS
  // ==========================================

  const handlePreviewMatter = async () => {
    if (!selectedMatter) {
      showToast.error('Please search and select a matter first');
      return;
    }

    try {
      const result = await previewMatterMutation.mutateAsync({
        matterId: selectedMatter.matterId,
        options: {
          dateFrom: matterDateFrom || undefined,
          dateTo: matterDateTo || undefined,
        },
      });

      setPreviewData(result);
      setShowPreview(true);
    } catch (error) {
      // Error handled in mutation hook
    }
  };

  const handleConfirmMatterSync = async () => {
    if (!selectedMatter) {
      showToast.error('Please search and select a matter first');
      return;
    }

    try {
      await syncMatterMutation.mutateAsync({
        matterId: selectedMatter.matterId,
        options: {
          dateFrom: matterDateFrom || undefined,
          dateTo: matterDateTo || undefined,
        },
      });

      setShowPreview(false);
      setPreviewData(null);
      setSelectedMatter(null);
      setMatterDateFrom('');
      setMatterDateTo('');
      await refetch();
    } catch (error) {
      // Error handled in mutation hook
    }
  };

  // ==========================================
  // BANK ACCOUNT SEARCH HANDLERS
  // ==========================================

  const handleSearchBankAccounts = async () => {
    if (!bankAccountSearchValue.trim()) {
      showToast.error('Please enter a search value');
      return;
    }

    try {
      const result = await searchBankAccountsMutation.mutateAsync({
        searchType: bankAccountSearchType,
        searchValue: bankAccountSearchValue.trim(),
        limit: 20,
      });

      setBankAccountSearchResults(result.bankAccounts);
      
      if (result.bankAccounts.length === 0) {
        showToast.info('No bank accounts found matching your search');
      } else {
        showToast.success(`Found ${result.bankAccounts.length} bank account(s)`);
      }
    } catch (error) {
      showToast.error('Failed to search bank accounts');
    }
  };

  const handleSelectBankAccount = (account: BankAccountSearchResult) => {
    setSelectedBankAccount(account);
    setBankAccountSearchResults([]);
    setBankAccountSearchValue('');
    showToast.success(`Selected: ${account.name} (${account.accountNumber || account.bankAccountId})`);
  };

  const handleClearBankAccountSelection = () => {
    setSelectedBankAccount(null);
    setPreviewData(null);
    setShowPreview(false);
  };

  // ==========================================
  // BANK ACCOUNT SYNC HANDLERS
  // ==========================================

  const handlePreviewBankAccount = async () => {
    if (!selectedBankAccount) {
      showToast.error('Please search and select a bank account first');
      return;
    }

    try {
      const result = await previewBankAccountMutation.mutateAsync({
        bankAccountId: selectedBankAccount.bankAccountId,
        options: {
          dateFrom: accountDateFrom || undefined,
          dateTo: accountDateTo || undefined,
          matterId: accountMatterId || undefined,
        },
      });

      setPreviewData(result);
      setShowPreview(true);
    } catch (error) {
      // Error handled in mutation hook
    }
  };

  const handleConfirmBankAccountSync = async () => {
    if (!selectedBankAccount) {
      showToast.error('Please search and select a bank account first');
      return;
    }

    try {
      await syncBankAccountMutation.mutateAsync({
        bankAccountId: selectedBankAccount.bankAccountId,
        options: {
          dateFrom: accountDateFrom || undefined,
          dateTo: accountDateTo || undefined,
          matterId: accountMatterId || undefined,
        },
      });

      setShowPreview(false);
      setPreviewData(null);
      setSelectedBankAccount(null);
      setAccountDateFrom('');
      setAccountDateTo('');
      setAccountMatterId('');
      await refetch();
    } catch (error) {
      // Error handled in mutation hook
    }
  };

  const handleCancelPreview = () => {
    setShowPreview(false);
    setPreviewData(null);
  };

  // ==========================================
  // INDIVIDUAL TRANSACTION RENDER
  // ==========================================

  const renderBankTransactionDetails = (transaction: any, source: string) => {
    return (
      <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
            Bank Transaction Details
          </h4>
          <Badge variant={source === 'dynamodb' ? 'success' : 'warning'}>
            {source === 'dynamodb' ? 'From XTND' : 'From Clio'}
          </Badge>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Transaction ID:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {transaction.bankTransactionId || transaction.id || 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Type:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {transaction.type || 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Date:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {transaction.date || 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Amount:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {transaction.amount !== undefined 
                ? `${transaction.currency || '$'}${transaction.amount.toLocaleString()}`
                : 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Description:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white truncate" title={transaction.description}>
              {transaction.description || 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Bank Account:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {transaction.bankAccountName || transaction.bank_account?.name || 'N/A'}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <PageLayout
      title="Clio Bank Transactions Replication"
      description="Manage and monitor Clio bank transactions data replication with enhanced preview"
      icon="HiDatabase"
      isLoading={isLoadingMetadata}
      breadcrumbs={[
        { label: 'Settings', href: '/settings' },
        { label: 'Data Replication' },
        { label: 'Clio Bank Transactions' },
      ]}
    >
      <div className="space-y-6">
        {/* Back button */}
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Icon name="HiArrowLeft" className="h-4 w-4" />}
          onClick={() => navigate('/settings')}
        >
          Back to Settings
        </Button>

        {/* Replication Status Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="HiInformationCircle" className="h-6 w-6 text-xtnd-blue" />
                <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
                  Replication Status
                </h3>
              </div>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
                onClick={() => refetch()}
              >
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            <div className={`grid grid-cols-1 ${canReset ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
              <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Icon name="HiCollection" className="h-8 w-8 text-xtnd-blue" />
                  <div>
                    <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
                      Total Bank Transactions
                    </p>
                    <p className="text-2xl font-bold text-xtnd-dark-800 dark:text-white">
                      {bankTransactionsCount.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Icon name="HiClock" className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
                      Last Replication
                    </p>
                    <p className="text-sm font-medium text-xtnd-dark-800 dark:text-white">
                      {formatDate(pickReplicationTimestamp(metadata))}
                    </p>
                  </div>
                </div>
              </div>

              {/* Reset Action - Only visible to authorized users */}
              {canReset && (
                <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-4">
                  <div className="flex flex-col space-y-2">
                    <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
                      Reset Replication
                    </p>
                    <p className="text-xs text-xtnd-dark-300 dark:text-xtnd-dark-400 mb-2">
                      Clear the last replication date to force a full sync
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResetMetadata}
                      loading={updateMetadataMutation.isPending}
                      leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
                    >
                      Reset to Force Full Sync
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Sync Mode Selection */}
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3">
              <Icon name="HiAdjustments" className="h-6 w-6 text-xtnd-blue" />
              <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
                Sync Mode
              </h3>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => setSyncMode('matter')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  syncMode === 'matter'
                    ? 'border-xtnd-blue bg-xtnd-light dark:bg-xtnd-dark-900'
                    : 'border-xtnd-dark-100 dark:border-xtnd-dark-600 hover:border-xtnd-blue'
                }`}
              >
                <Icon name="HiDocumentText" className="h-8 w-8 text-xtnd-blue mx-auto mb-2" />
                <h4 className="font-medium text-xtnd-dark-800 dark:text-white">Sync by Matter</h4>
                <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                  Sync all transactions for a specific matter
                </p>
              </button>

              <button
                onClick={() => setSyncMode('bankAccount')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  syncMode === 'bankAccount'
                    ? 'border-xtnd-blue bg-xtnd-light dark:bg-xtnd-dark-900'
                    : 'border-xtnd-dark-100 dark:border-xtnd-dark-600 hover:border-xtnd-blue'
                }`}
              >
                <Icon name="HiCreditCard" className="h-8 w-8 text-xtnd-blue mx-auto mb-2" />
                <h4 className="font-medium text-xtnd-dark-800 dark:text-white">Sync by Bank Account</h4>
                <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                  Sync all transactions for a bank account
                </p>
              </button>

              <button
                onClick={() => setSyncMode('individual')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  syncMode === 'individual'
                    ? 'border-xtnd-blue bg-xtnd-light dark:bg-xtnd-dark-900'
                    : 'border-xtnd-dark-100 dark:border-xtnd-dark-600 hover:border-xtnd-blue'
                }`}
              >
                <Icon name="HiSearch" className="h-8 w-8 text-xtnd-blue mx-auto mb-2" />
                <h4 className="font-medium text-xtnd-dark-800 dark:text-white">Sync Individual</h4>
                <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                  Search and sync a single transaction
                </p>
              </button>
            </div>
          </CardBody>
        </Card>

        {/* Sync by Matter */}
        {syncMode === 'matter' && (
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3">
                <Icon name="HiDocumentText" className="h-6 w-6 text-xtnd-blue" />
                <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
                  Sync by Matter
                </h3>
              </div>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                {/* Matter Search */}
                {!selectedMatter && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                          Search Type
                        </label>
                        <Select
                          value={matterSearchType}
                          onChange={(e) => setMatterSearchType(e.target.value as MatterSearchType)}
                        >
                          <option value="displayNumber">Matter Display Number</option>
                          <option value="matterId">Matter ID</option>
                          <option value="number">Matter Number</option>
                        </Select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                          Search Value
                        </label>
                        <div className="flex space-x-2">
                          <Input
                            value={matterSearchValue}
                            onChange={(e) => setMatterSearchValue(e.target.value)}
                            placeholder={`Enter ${matterSearchType === 'displayNumber' ? 'display number' : matterSearchType === 'matterId' ? 'matter ID' : 'matter number'}...`}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearchMatters()}
                          />
                          <Button
                            variant="primary"
                            onClick={handleSearchMatters}
                            loading={searchMattersMutation.isPending}
                            leftIcon={<Icon name="HiSearch" className="h-4 w-4" />}
                          >
                            Search
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Search Results */}
                    {matterSearchResults.length > 0 && (
                      <div className="border border-xtnd-dark-100 dark:border-xtnd-dark-600 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600">
                            <thead className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                                  Display Number
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                                  Description
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                                  Client
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-xtnd-dark-800 divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600">
                              {matterSearchResults.map((matter) => (
                                <tr key={matter.matterId} className="hover:bg-xtnd-dark-50 dark:hover:bg-xtnd-dark-700">
                                  <td className="px-4 py-3 text-sm text-xtnd-dark-800 dark:text-white">
                                    {matter.displayNumber}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-xtnd-dark-800 dark:text-white">
                                    {matter.description || 'N/A'}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    <Badge variant={matter.status === 'Open' ? 'success' : 'default'}>
                                      {matter.status}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-xtnd-dark-800 dark:text-white">
                                    {matter.clientName || 'N/A'}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    <Button
                                      size="sm"
                                      variant="primary"
                                      onClick={() => handleSelectMatter(matter)}
                                    >
                                      Select
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Selected Matter */}
                {selectedMatter && (
                  <div className="bg-xtnd-light dark:bg-xtnd-dark-900 border border-xtnd-blue rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center space-x-2">
                          <Icon name="HiCheckCircle" className="h-5 w-5 text-xtnd-blue" />
                          <span className="font-medium text-xtnd-dark-800 dark:text-white">
                            Selected Matter: {selectedMatter.displayNumber}
                          </span>
                        </div>
                        <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
                          {selectedMatter.description} • {selectedMatter.clientName}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleClearMatterSelection}
                      >
                        Change
                      </Button>
                    </div>
                  </div>
                )}

                {/* Date Filters (only show when matter is selected) */}
                {selectedMatter && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                        Date From (Optional)
                      </label>
                      <Input
                        type="date"
                        value={matterDateFrom}
                        onChange={(e) => setMatterDateFrom(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                        Date To (Optional)
                      </label>
                      <Input
                        type="date"
                        value={matterDateTo}
                        onChange={(e) => setMatterDateTo(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                {selectedMatter && (
                  <div className="flex space-x-3">
                    <Button
                      variant="secondary"
                      onClick={handlePreviewMatter}
                      loading={previewMatterMutation.isPending}
                      leftIcon={<Icon name="HiEye" className="h-4 w-4" />}
                    >
                      Preview Sync
                    </Button>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Sync by Bank Account */}
        {syncMode === 'bankAccount' && (
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3">
                <Icon name="HiCreditCard" className="h-6 w-6 text-xtnd-blue" />
                <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
                  Sync by Bank Account
                </h3>
              </div>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                {/* Bank Account Search */}
                {!selectedBankAccount && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                          Search Type
                        </label>
                        <Select
                          value={bankAccountSearchType}
                          onChange={(e) => setBankAccountSearchType(e.target.value as BankAccountSearchType)}
                        >
                          <option value="name">Bank Account Name</option>
                          <option value="bankAccountId">Bank Account ID</option>
                          <option value="accountNumber">Account Number</option>
                        </Select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                          Search Value
                        </label>
                        <div className="flex space-x-2">
                          <Input
                            value={bankAccountSearchValue}
                            onChange={(e) => setBankAccountSearchValue(e.target.value)}
                            placeholder={`Enter ${bankAccountSearchType === 'name' ? 'account name' : bankAccountSearchType === 'bankAccountId' ? 'account ID' : 'account number'}...`}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearchBankAccounts()}
                          />
                          <Button
                            variant="primary"
                            onClick={handleSearchBankAccounts}
                            loading={searchBankAccountsMutation.isPending}
                            leftIcon={<Icon name="HiSearch" className="h-4 w-4" />}
                          >
                            Search
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Search Results */}
                    {bankAccountSearchResults.length > 0 && (
                      <div className="border border-xtnd-dark-100 dark:border-xtnd-dark-600 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600">
                            <thead className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                                  Name
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                                  Account Number
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                                  Type
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                                  Balance
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                                  Institution
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-xtnd-dark-800 divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600">
                              {bankAccountSearchResults.map((account) => (
                                <tr key={account.bankAccountId} className="hover:bg-xtnd-dark-50 dark:hover:bg-xtnd-dark-700">
                                  <td className="px-4 py-3 text-sm text-xtnd-dark-800 dark:text-white">
                                    {account.name}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-xtnd-dark-800 dark:text-white">
                                    {account.accountNumber || 'N/A'}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    <Badge variant="default">
                                      {account.type || 'N/A'}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-xtnd-dark-800 dark:text-white">
                                    ${(account.balance || 0).toFixed(2)}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-xtnd-dark-800 dark:text-white">
                                    {account.institution || 'N/A'}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    <Button
                                      size="sm"
                                      variant="primary"
                                      onClick={() => handleSelectBankAccount(account)}
                                    >
                                      Select
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Selected Bank Account */}
                {selectedBankAccount && (
                  <div className="bg-xtnd-light dark:bg-xtnd-dark-900 border border-xtnd-blue rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center space-x-2">
                          <Icon name="HiCheckCircle" className="h-5 w-5 text-xtnd-blue" />
                          <span className="font-medium text-xtnd-dark-800 dark:text-white">
                            Selected Bank Account: {selectedBankAccount.name}
                          </span>
                        </div>
                        <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
                          {selectedBankAccount.accountNumber ? `Account: ${selectedBankAccount.accountNumber}` : `ID: ${selectedBankAccount.bankAccountId}`} • {selectedBankAccount.institution}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleClearBankAccountSelection}
                      >
                        Change
                      </Button>
                    </div>
                  </div>
                )}

                {/* Date Filters and Matter Filter (only show when bank account is selected) */}
                {selectedBankAccount && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                        Date From (Optional)
                      </label>
                      <Input
                        type="date"
                        value={accountDateFrom}
                        onChange={(e) => setAccountDateFrom(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                        Date To (Optional)
                      </label>
                      <Input
                        type="date"
                        value={accountDateTo}
                        onChange={(e) => setAccountDateTo(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                        Matter ID Filter (Optional)
                      </label>
                      <Input
                        value={accountMatterId}
                        onChange={(e) => setAccountMatterId(e.target.value)}
                        placeholder="Filter by Matter ID"
                      />
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                {selectedBankAccount && (
                  <div className="flex space-x-3">
                    <Button
                      variant="secondary"
                      onClick={handlePreviewBankAccount}
                      loading={previewBankAccountMutation.isPending}
                      leftIcon={<Icon name="HiEye" className="h-4 w-4" />}
                    >
                      Preview Sync
                    </Button>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Individual Transaction Search (existing functionality) */}
        {syncMode === 'individual' && (
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3">
                <Icon name="HiSearch" className="h-6 w-6 text-xtnd-blue" />
                <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
                  Search & Sync Individual Transaction
                </h3>
              </div>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="sm:w-48">
                    <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                      Search By
                    </label>
                    <select
                      value={searchType}
                      onChange={(e) => setSearchType(e.target.value as BankTransactionSearchType)}
                      className="w-full px-3 py-2 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md bg-white dark:bg-xtnd-dark-700 text-xtnd-dark-800 dark:text-white focus:ring-xtnd-blue focus:border-xtnd-blue"
                    >
                      <option value="bankTransactionId">Transaction ID</option>
                      <option value="date">Date</option>
                      <option value="type">Type</option>
                    </select>
                  </div>

                  <div className="flex-1">
                    <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                      Search Value
                    </label>
                    <Input
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      placeholder="Enter search value..."
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                  </div>

                  <div className="flex items-end">
                    <Button
                      variant="primary"
                      onClick={handleSearch}
                      loading={isSearching}
                      leftIcon={<Icon name="HiSearch" className="h-4 w-4" />}
                    >
                      Search
                    </Button>
                  </div>
                </div>

                {/* Search Results */}
                {hasSearched && (
                  <div className="mt-6 space-y-4">
                    {isSearching && (
                      <div className="text-center py-8">
                        <Icon name="HiRefresh" className="h-8 w-8 text-xtnd-blue animate-spin mx-auto" />
                        <p className="mt-2 text-xtnd-dark-400 dark:text-xtnd-dark-300">Searching...</p>
                      </div>
                    )}

                    {!isSearching && dynamoDBResult && dynamoDBResult.found && (
                      <div className="space-y-4">
                        {renderBankTransactionDetails(dynamoDBResult.bankTransaction, 'dynamodb')}
                        <div className="flex justify-end">
                          <Button
                            variant="secondary"
                            onClick={() => handleSyncBankTransaction(
                              dynamoDBResult.bankTransaction?.bankTransactionId || 
                              dynamoDBResult.bankTransaction?.id?.toString() || 
                              searchValue
                            )}
                            loading={syncBankTransactionMutation.isPending}
                            leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
                          >
                            Resync from Clio
                          </Button>
                        </div>
                      </div>
                    )}

                    {!isSearching && !dynamoDBResult?.found && clioResult && clioResult.found && (
                      <div className="space-y-4">
                        {renderBankTransactionDetails(clioResult.bankTransaction, 'clio')}
                        <div className="flex justify-end">
                          <Button
                            variant="primary"
                            onClick={() => handleSyncBankTransaction(
                              clioResult.bankTransaction?.id?.toString() || searchValue
                            )}
                            loading={syncBankTransactionMutation.isPending}
                            leftIcon={<Icon name="HiDownload" className="h-4 w-4" />}
                          >
                            Replicate to Local Cache
                          </Button>
                        </div>
                      </div>
                    )}

                    {!isSearching && !dynamoDBResult?.found && !clioResult?.found && clioResult !== null && (
                      <div className="text-center py-8">
                        <Icon name="HiSearchCircle" className="h-12 w-12 text-xtnd-dark-300 mx-auto" />
                        <p className="mt-2 text-xtnd-dark-400 dark:text-xtnd-dark-300">
                          No transaction found
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Preview Modal/Card */}
        {showPreview && previewData && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Icon name="HiEye" className="h-6 w-6 text-xtnd-blue" />
                  <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
                    Sync Preview
                  </h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelPreview}
                  leftIcon={<Icon name="HiX" className="h-4 w-4" />}
                >
                  Cancel
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <div className="space-y-6">
                {/* Summary */}
                <div className="bg-xtnd-light dark:bg-xtnd-dark-900 rounded-lg p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <Icon name="HiPlus" className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">New Records</p>
                      <p className="text-3xl font-bold text-green-600">{previewData.totalNewRecords}</p>
                    </div>
                    <div className="text-center">
                      <Icon name="HiRefresh" className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                      <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">Already Synced</p>
                      <p className="text-3xl font-bold text-yellow-600">{previewData.totalExistingRecords}</p>
                    </div>
                    <div className="text-center">
                      <Icon name="HiDatabase" className="h-8 w-8 text-xtnd-blue mx-auto mb-2" />
                      <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">Last 5 Local</p>
                      <p className="text-3xl font-bold text-xtnd-blue">{previewData.lastFiveLocal.length}</p>
                    </div>
                  </div>
                </div>

                {/* New Transactions Table */}
                {previewData.totalNewRecords > 0 && (
                  <BankTransactionPreviewTable
                    transactions={previewData.newTransactions}
                    title="New Transactions (Will be synced)"
                    variant="new"
                  />
                )}

                {/* Already Synced Transactions */}
                {previewData.totalExistingRecords > 0 && (
                  <BankTransactionPreviewTable
                    transactions={previewData.existingTransactions.slice(0, 10)}
                    title="Already Synced Transactions (No action needed)"
                    variant="existing"
                    maxHeight="300px"
                  />
                )}

                {/* Last 5 Local Records */}
                {previewData.lastFiveLocal.length > 0 && (
                  <BankTransactionPreviewTable
                    transactions={previewData.lastFiveLocal}
                    title="Last 5 Records in Local Database"
                    variant="local"
                    maxHeight="250px"
                  />
                )}

                {/* Confirm/Cancel Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-xtnd-dark-100 dark:border-xtnd-dark-600">
                  <Button
                    variant="outline"
                    onClick={handleCancelPreview}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={
                      syncMode === 'matter'
                        ? handleConfirmMatterSync
                        : handleConfirmBankAccountSync
                    }
                    loading={
                      syncMode === 'matter'
                        ? syncMatterMutation.isPending
                        : syncBankAccountMutation.isPending
                    }
                    leftIcon={<Icon name="HiCheck" className="h-4 w-4" />}
                    disabled={previewData.totalNewRecords === 0}
                  >
                    Confirm Sync ({previewData.totalNewRecords} new records)
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </PageLayout>
  );
}
